import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Trash2 } from "lucide-react";
import { generateReceiptPdf } from "@/lib/receipt";

export default function ReceiptsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receipts");
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this receipt? This can't be undone.")) return;
    try {
      await api.delete(`/receipts/${id}`);
      toast.success("Deleted");
      load();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="receipts-tab">
      <div className="mb-6">
        <h3 className="font-heading font-black text-3xl tracking-tighter">
          Fee Receipts
        </h3>
        <p className="text-sm font-medium text-[#4A4A4A] mt-1">
          Every fee-paid update auto-generates a receipt. Download or delete below. Customize the template from the Template tab.
        </p>
      </div>

      <div className="brutal-card p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="receipts-table">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Receipt #</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Date</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Student</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Room</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Amount</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest">Balance Due</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center font-medium text-[#4A4A4A]">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-10 text-center font-medium text-[#4A4A4A]">
                    No receipts yet. When admin records a payment, a receipt is auto-created.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t-2 border-[#0A0A0A] hover:bg-[#FFFCE0]"
                  data-testid={`receipt-row-${r.id}`}
                >
                  <td className="px-4 py-3 font-mono font-bold">{r.receipt_number}</td>
                  <td className="px-4 py-3">
                    {new Date(r.payment_date || r.issued_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-4 py-3 font-bold">{r.student_name}</td>
                  <td className="px-4 py-3">{r.flat_number || "-"}</td>
                  <td className="px-4 py-3 font-bold">
                    ₹{Number(r.amount).toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-3 py-1 border-2 border-[#0A0A0A] rounded-full text-xs font-bold ${
                        Number(r.balance_due) <= 0
                          ? "bg-[#00CC66] text-white"
                          : "bg-[#FF4D4D] text-white"
                      }`}
                    >
                      ₹{Number(r.balance_due).toLocaleString("en-IN")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => generateReceiptPdf(r)}
                        data-testid={`receipt-download-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 font-bold text-xs border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white hover:shadow-brutal-sm transition-shadow"
                      >
                        <Download size={13} strokeWidth={2.5} /> PDF
                      </button>
                      <button
                        onClick={() => remove(r.id)}
                        data-testid={`receipt-delete-${r.id}`}
                        className="p-2 border-2 border-[#0A0A0A] rounded-full bg-[#FF4D4D] text-white hover:shadow-brutal-sm transition-shadow"
                      >
                        <Trash2 size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
